import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RegisterProps } from "../../types/screensTypes";
import { getCompanies, registerAdmin } from "../../api";
import Dropdown from "../../components/common/Dropdown";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import Button from "../../components/ui/Button";
import TextInput from "../../components/ui/TextInput";
import { validatePassword } from "../../utils/passwordValidation";
import styles from "./Register.module.css";

interface Company {
    label: string;
    value: string;
}

const Register: React.FC<RegisterProps> = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [company, setCompany] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [companies, setCompanies] = useState<Company[]>([]);
    const [registrationSuccess, setRegistrationSuccess] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [loading] = useState<boolean>(false);

    const passwordValidation = useMemo(() => validatePassword(password), [password]);
    const showPasswordRequirements = password.length > 0;

    const handleRegister = async () => {
        try {
            await registerAdmin({ email, password, company });
            setRegistrationSuccess(true);
        } catch (error) {
            console.error("Error registering:", error);
            setErrorMessage(t("registrationFailed"));
        }
    };

    const getCompaniesAPI = async () => {
        try {
            const response = await getCompanies();
            if (response) {
                const companiesList: Company[] = response.map((company: { name: string; id: string }) => ({
                    label: company.name,
                    value: company.id,
                }));
                setCompanies(companiesList);
            }
        } catch (error) {
            console.error("Error getting companies:", error);
        }
    };

    useEffect(() => {
        getCompaniesAPI();
    }, []);

    const isFormComplete = company.trim() !== "" && email.trim() !== "" && passwordValidation.isValid;

    return (
        <div className={styles.container}>
            {registrationSuccess ? (
                <div className="success-message">
                    <TitleSubtitle
                        title={t("thanksForRegistering")}
                        subtitle={t("youWillReceiveAnEmailFromTheRewindTeamOnceYourAccountIsValidated")}
                    />
                    <Button label={t("backToLogin")} variant="primary" onClick={() => navigate("/login")} />
                </div>
            ) : (
                <div>
                    <TitleSubtitle title={t("createYourAccount")} subtitle="" />

                    <div className={styles.form}>
                        <Dropdown
                            label=""
                            options={companies}
                            onChange={(value) => setCompany(value)}
                            placeholder={t("companyName")}
                            loading={loading}
                            error={errorMessage ? true : false}
                            value={company}
                        />
                        <div style={{ marginTop: "-15px" }}>
                            <TextInput
                                type="email"
                                placeholder={t("enterYourEmailMandatory")}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <TextInput
                            type="password"
                            placeholder={t("enterYourPasswordMandatory")}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />

                        {showPasswordRequirements && (
                            <div
                                className="password-requirements"
                                style={{ fontSize: "12px", marginTop: "8px", marginBottom: "8px" }}
                            >
                                <p style={{ margin: "0 0 4px 0", fontWeight: 500 }}>{t("passwordRequirements")}:</p>
                                <ul style={{ margin: 0, paddingLeft: "20px", listStyle: "none" }}>
                                    <li style={{ color: passwordValidation.requirements.minLength ? "green" : "red" }}>
                                        {passwordValidation.requirements.minLength ? "✓" : "✗"} {t("passwordMinLength")}
                                    </li>
                                    <li
                                        style={{
                                            color: passwordValidation.requirements.hasUppercase ? "green" : "red",
                                        }}
                                    >
                                        {passwordValidation.requirements.hasUppercase ? "✓" : "✗"}{" "}
                                        {t("passwordUppercase")}
                                    </li>
                                    <li
                                        style={{
                                            color: passwordValidation.requirements.hasLowercase ? "green" : "red",
                                        }}
                                    >
                                        {passwordValidation.requirements.hasLowercase ? "✓" : "✗"}{" "}
                                        {t("passwordLowercase")}
                                    </li>
                                    <li style={{ color: passwordValidation.requirements.hasNumber ? "green" : "red" }}>
                                        {passwordValidation.requirements.hasNumber ? "✓" : "✗"} {t("passwordNumber")}
                                    </li>
                                    <li style={{ color: passwordValidation.requirements.hasSpecial ? "green" : "red" }}>
                                        {passwordValidation.requirements.hasSpecial ? "✓" : "✗"} {t("passwordSpecial")}
                                    </li>
                                </ul>
                            </div>
                        )}

                        {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}

                        <Button
                            label={t("register")}
                            variant="primary"
                            onClick={handleRegister}
                            disabled={!isFormComplete}
                        />

                        <p className={styles.terms}>
                            {t("byClickingYouAccept")}{" "}
                            <button type="button" className={styles.termsLink}>
                                {t("terms")}
                            </button>{" "}
                            {t("andOur")}{" "}
                            <button type="button" className={styles.termsLink}>
                                {t("dataPolicy")}
                            </button>
                            .
                        </p>

                        <div className={styles.footer}>
                            <p>
                                {t("alreadyHaveAnAccount")} <a href="/login">{t("login")}</a>
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Register;
